/** @format */

import React from "react";
import { Box, Typography, CircularProgress, LinearProgress } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { IRecoveryHealth } from "../../../utility/types/collectTypes";
import { colors } from "../../../utility/types/constant";
import {
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
} from "../styles";

// ─────────────────────────────────────────────────────────────────────────
// RecoveryHealthPanel — composite collections-health score (0–100) as a
// radial gauge, with the four contributing factors broken out below.
// Reads live data from GET /collect/analytics/health.
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    health: IRecoveryHealth | null;
    isLoading: boolean;
}

const COMPONENT_META: {
    key: keyof IRecoveryHealth["components"];
    label: string;
    hint: string;
}[] = [
        {
            key: "overduePressure",
            label: "Overdue pressure",
            hint: "Share of open balance not yet overdue",
        },
        {
            key: "collectedRatio",
            label: "Collected ratio",
            hint: "Tracked invoices already recovered",
        },
        {
            key: "promiseReliability",
            label: "Promise reliability",
            hint: "Promises kept vs broken",
        },
        {
            key: "manualReviewLoad",
            label: "Manual-review load",
            hint: "Accounts not needing a human",
        },
    ];

/** Map a 0–100 score to a traffic-light colour. */
function scoreColor(score: number): string {
    if (score >= 75) return colors.mainSecondary; // green
    if (score >= 50) return "#f59e0b"; // amber
    return "#ef4444"; // red
}

function scoreLabel(score: number): string {
    if (score >= 75) return "Healthy";
    if (score >= 50) return "Needs attention";
    return "At risk";
}

const RecoveryHealthPanel: React.FC<Props> = ({ health, isLoading }) => {
    const score = health?.healthScore ?? 0;
    const gaugeColor = scoreColor(score);

    const options: ApexOptions = {
        chart: {
            type: "radialBar",
            fontFamily: "'Montserrat', sans-serif",
            sparkline: { enabled: true },
        },
        plotOptions: {
            radialBar: {
                hollow: { size: "62%" },
                track: { background: "#eef0f4" },
                dataLabels: {
                    name: {
                        offsetY: 22,
                        fontSize: "0.8rem",
                        color: "#6b7280",
                    },
                    value: {
                        offsetY: -12,
                        fontSize: "2rem",
                        fontWeight: 800,
                        color: colors.primary,
                        formatter: (val: number) => `${Math.round(val)}`,
                    },
                },
            },
        },
        colors: [gaugeColor],
        fill: { colors: [gaugeColor] },
        stroke: { lineCap: "round" },
        labels: [scoreLabel(score)],
    };

    return (
        <CollectSectionCard sx={{ p: 3, height: "100%" }}>
            <CollectSectionTitle>Collections health</CollectSectionTitle>
            <CollectSectionSubtitle>
                Composite score across overdue pressure, recovery, promises and
                review load.
            </CollectSectionSubtitle>

            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                    }}
                >
                    <CircularProgress size={28} sx={{ color: colors.mainSecondary }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ mt: 1 }}>
                        <ReactApexChart
                            options={options}
                            series={[score]}
                            type="radialBar"
                            height={220}
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        {COMPONENT_META.map((c) => {
                            const value = health?.components?.[c.key] ?? 0;
                            return (
                                <Box key={c.key} sx={{ mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "baseline",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 600,
                                                fontSize: "0.8rem",
                                                color: "#374151",
                                            }}
                                        >
                                            {c.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.8rem",
                                                color: scoreColor(value),
                                            }}
                                        >
                                            {value}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(100, Math.max(0, value))}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: "#eef0f4",
                                            "& .MuiLinearProgress-bar": {
                                                backgroundColor: scoreColor(value),
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.68rem",
                                            color: "#9ca3af",
                                            mt: 0.25,
                                        }}
                                    >
                                        {c.hint}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </CollectSectionCard>
    );
};

export default RecoveryHealthPanel;
